// Shootout — alternating-breakaway tiebreaker after a tied OT.
//
// Owns:   the post-OT shootout round / attempt machine. Each attempt
//         puts a single shooter alone against the defending goalie at
//         centre ice; first goal of the attempt wins the round for
//         that team.
// Reads:  state.score, state.players, state.goalies, state.tick.
// Mutates: state.rules.shootout, state.puck, state.players (bench/un-bench
//          per-attempt), state.clock (phase=GameOver at end), state.score
//          when an attempt scores (the regulation+OT score is held; the
//          shootout score is tracked separately on ShootoutState).
// Emits:  Stoppage (between attempts via positioning, not via
//         enterStoppage) and MatchEnded at the end.
//
// Attempt structure:
//   - Setup (~30 ticks): bench everyone except the shooter and the
//     defending goalie; centre the puck; place the shooter ~10m in
//     front of centre on a straight line to the goal.
//   - Shoot (~270 ticks at most): bot shooter drives the goal and
//     shoots when in range. Goal scored → record goal, end attempt
//     early. Save / miss / timer expiry → record miss.
//   - Result (~30 ticks): brief pause showing the outcome before the
//     next attempt starts.
//
// First 3 rounds (6 attempts: H-A-H-A-H-A) are scheduled in advance.
// If still tied, sudden-death continues one pair at a time until the
// score differs after a completed pair (home + away both shot).
//
// Zero-allocation.

import type { PlayerId, TeamId } from "@arcade-hockey/shared";
import { playerId } from "@arcade-hockey/shared";
import { tuning } from "../state/tuning.js";
import type { MatchState, PlayerState, ShootoutState } from "../state/types.js";

const SETUP_TICKS = 30;
const SHOOT_TICKS = 270;
const RESULT_TICKS = 30;

/**
 * Enter the shootout phase from OT-tied. Called from
 * `core/SimTick.ts`'s Overtime → end branch when the timer expires.
 * Initialises ShootoutState and stages the first attempt's Setup.
 */
export function enterShootout(state: MatchState): void {
  state.clock.phase = "Shootout";
  // remainingTicks isn't really used here — the shootout drives itself
  // via subPhaseEndTick. Park it at 0 so any stray clock-tick paths
  // don't reuse it.
  state.clock.remainingTicks = 0;
  state.clock.nextPlayPhase = undefined;

  const shooterTeam: TeamId = "home";
  const shooterId = pickShooter(state, shooterTeam, 1);
  state.rules.shootout = {
    round: 1,
    shooterTeam,
    shooterId,
    subPhase: "Setup",
    subPhaseEndTick: state.tick + SETUP_TICKS,
    lastResult: "None",
    score: { home: 0, away: 0 },
    attempts: { home: 0, away: 0 },
    shotTaken: false,
    shotTakenAtTick: -1,
    maxPuckForward: Number.NEGATIVE_INFINITY,
  };
  stageAttempt(state, state.rules.shootout);
}

/**
 * Per-tick shootout driver. Wired into `core/SimTick.ts` from the
 * Shootout phase branch.
 */
export function tickShootout(state: MatchState): void {
  if (state.clock.phase !== "Shootout") return;
  const so = state.rules.shootout;
  if (so === undefined) return;

  // Detect goal during Shoot sub-phase by inspecting the score delta
  // since the attempt started. We use the per-attempt baseline stored
  // implicitly: the shooter's team should not have scored prior to the
  // attempt because the shootout doesn't change `state.score` — the
  // goal lands in `state.score` because `tickGoalDetection` fires
  // independently. So we observe via the GoalScored event at this tick.
  if (so.subPhase === "Shoot") {
    // Goal check first — a same-tick GoalScored always closes the
    // attempt (the rest of the rules don't apply if the puck went in).
    if (didShooterScore(state, so.shooterTeam)) {
      so.lastResult = "Goal";
      if (so.shooterTeam === "home") so.score.home += 1;
      else so.score.away += 1;
      endAttempt(state, so);
      return;
    }

    // Detect a fresh ShotReleased from the current shooter — locks
    // the attempt into the "one-shot-only" post-shot phase.
    if (!so.shotTaken) {
      for (const ev of state.events) {
        if (ev.type === "ShotReleased" && ev.tick === state.tick && ev.from === so.shooterId) {
          so.shotTaken = true;
          so.shotTakenAtTick = state.tick;
          break;
        }
      }
    }

    // Puck-must-move-forward rule. Track the puck's max forward
    // distance (signed by shooter team) and end the attempt if the
    // puck regresses by more than the tolerance.
    const forward = so.shooterTeam === "home" ? state.puck.pos.x : -state.puck.pos.x;
    if (forward > so.maxPuckForward) so.maxPuckForward = forward;
    if (
      Number.isFinite(so.maxPuckForward) &&
      so.maxPuckForward - forward > tuning.clock.shootoutMaxRegression
    ) {
      so.lastResult = "Miss";
      endAttempt(state, so);
      return;
    }

    // Post-shot resolution window: if a shot was taken and the puck
    // hasn't gone in within `shootoutPostShotTicks`, record a Miss.
    if (so.shotTaken && state.tick - so.shotTakenAtTick >= tuning.clock.shootoutPostShotTicks) {
      so.lastResult = "Miss";
      endAttempt(state, so);
      return;
    }

    if (state.tick >= so.subPhaseEndTick) {
      // Attempt-length timeout (no shot taken at all) — miss.
      so.lastResult = "Miss";
      endAttempt(state, so);
      return;
    }
    return;
  }

  if (so.subPhase === "Setup") {
    if (state.tick >= so.subPhaseEndTick) {
      so.subPhase = "Shoot";
      so.subPhaseEndTick = state.tick + SHOOT_TICKS;
    }
    return;
  }

  // Result
  if (so.subPhase === "Result") {
    if (state.tick < so.subPhaseEndTick) return;

    // Decide what comes next: another attempt, or game over.
    if (canDecideWinner(so)) {
      const winner = decideWinner(so);
      state.clock.phase = "GameOver";
      state.events.push({ type: "MatchEnded", tick: state.tick, winner });
      // Bake the shootout result into the visible scoreboard.
      if (winner === "home") state.score.home += 1;
      else if (winner === "away") state.score.away += 1;
      return;
    }

    // Schedule the next attempt — alternate shooter team.
    const nextShooterTeam: TeamId = so.shooterTeam === "home" ? "away" : "home";
    // Advance round when home + away have both shot this round.
    const sameRound = so.attempts.home <= so.attempts.away ? so.attempts.home : so.attempts.away;
    so.round = sameRound + 1; // round number = #completed rounds + 1
    so.shooterTeam = nextShooterTeam;
    so.shooterId = pickShooter(state, nextShooterTeam, so.attempts[nextShooterTeam] + 1);
    so.subPhase = "Setup";
    so.subPhaseEndTick = state.tick + SETUP_TICKS;
    so.lastResult = "None";
    so.shotTaken = false;
    so.shotTakenAtTick = -1;
    so.maxPuckForward = Number.NEGATIVE_INFINITY;
    stageAttempt(state, so);
  }
}

/** Did the current shooter's team score this tick? */
function didShooterScore(state: MatchState, shooterTeam: TeamId): boolean {
  for (const ev of state.events) {
    if (ev.type !== "GoalScored") continue;
    if (ev.tick !== state.tick) continue;
    if (ev.teamId === shooterTeam) return true;
  }
  return false;
}

function endAttempt(state: MatchState, so: ShootoutState): void {
  so.attempts[so.shooterTeam] += 1;
  so.subPhase = "Result";
  so.subPhaseEndTick = state.tick + RESULT_TICKS;
  // Park the puck off-ice so a save/miss doesn't drift back into the
  // crease during the Result window.
  state.puck.pos.x = 0;
  state.puck.pos.y = 0;
  state.puck.vel.x = 0;
  state.puck.vel.y = 0;
  state.puck.mode = "Loose";
  state.puck.ownerId = null;
}

/**
 * Have we played enough attempts to decide a winner? Two conditions:
 *   - First 3 rounds (6 attempts) are done AND the score differs.
 *   - Sudden death: after each completed pair (round 4+), if the score
 *     differs the trailing team can't catch up.
 */
function canDecideWinner(so: ShootoutState): boolean {
  const totalAttempts = so.attempts.home + so.attempts.away;
  if (so.attempts.home !== so.attempts.away) return false;
  if (so.score.home === so.score.away) return false;
  if (totalAttempts >= 6) return true;
  return false;
}

function decideWinner(so: ShootoutState): TeamId | "Draw" {
  if (so.score.home > so.score.away) return "home";
  if (so.score.away > so.score.home) return "away";
  return "Draw";
}

function pickShooter(state: MatchState, team: TeamId, attemptNumber: number): PlayerId {
  // Rotate through home-1 → home-5 → home-1 → ... (1-indexed).
  const idx = ((attemptNumber - 1) % 5) + 1;
  const id = playerId(`${team}-${idx}`);
  // Verify it exists in the lookup map; fall back to home-1/away-1 if
  // something's off.
  if (state.playersById.has(id)) return id;
  return playerId(`${team}-1`);
}

/**
 * Stage an attempt: bench everyone except the shooter, un-bench the
 * shooter, centre the puck, place the shooter for the breakaway. Run
 * once per attempt at Setup-start.
 */
function stageAttempt(state: MatchState, so: ShootoutState): void {
  const shooter = state.playersById.get(so.shooterId);
  if (shooter === undefined) return;

  // Bench everyone — wings, defensemen, every non-shooter centre.
  for (const p of state.players) {
    if (p.id === so.shooterId) {
      p.benched = false;
      // Place the shooter at centre ice facing the defending goal.
      const facing = so.shooterTeam === "home" ? 1 : -1;
      p.pos.x = -facing * 8; // ~8m back from centre, on the shooter's side
      p.pos.y = 0;
      p.vel.x = 0;
      p.vel.y = 0;
      p.faceDir.x = facing;
      p.faceDir.y = 0;
      p.stickDir.x = facing;
      p.stickDir.y = 0;
      p.action = "Idle";
      p.actionTimer = 0;
      p.stunTicks = 0;
      p.recoveryTicks = 0;
      p.stamina = 1.0;
      p.hasPuck = false;
      p.possessionQuality = 0;
      p.shootHoldTicks = 0;
      p.passHoldTicks = 0;
      p.receivedPassFromTick = -1;
      p.isProtecting = false;
    } else {
      benchPlayer(p);
    }
  }

  // Centre the puck.
  state.puck.pos.x =
    -8 * (so.shooterTeam === "home" ? 1 : -1) + (so.shooterTeam === "home" ? 0.9 : -0.9);
  state.puck.pos.y = 0;
  state.puck.vel.x = 0;
  state.puck.vel.y = 0;
  state.puck.mode = "Controlled";
  state.puck.ownerId = so.shooterId;
  state.puck.lastTouchedBy = so.shooterId;
  state.puck.noRecaptureUntilTick = 0;
  state.puck.opponentLockoutPlayerId = null;
  state.puck.opponentLockoutUntilTick = 0;

  // Shooter gets puck pinned to stick (Controlled).
  shooter.hasPuck = true;
  shooter.possessionQuality = 0.8;

  // Goalie: defending team's goalie un-pulled, in arc. Other goalie:
  // doesn't matter for play, but un-pull for state consistency.
  for (const g of state.goalies) {
    g.pulled = false;
    g.mode = "Ready";
    g.modeTimer = 0;
    const halfL = tuning.rink.length / 2;
    const sign = g.teamId === "home" ? -1 : 1;
    g.pos.x = sign * (halfL - tuning.rink.goalLineOffset - tuning.goalie.arcDepthFar);
    g.pos.y = 0;
    g.vel.x = 0;
    g.vel.y = 0;
    g.facing.x = -sign;
    g.facing.y = 0;
  }
}

function benchPlayer(p: PlayerState): void {
  p.benched = true;
  const halfW = tuning.rink.width / 2;
  const y = halfW + 1.5;
  const teamSign = p.teamId === "home" ? -1 : 1;
  // Spread by role so they don't overlap.
  const slot =
    p.position === "C"
      ? 0
      : p.position === "LW"
        ? 4
        : p.position === "RW"
          ? 8
          : p.position === "LD"
            ? 12
            : 16;
  p.pos.x = teamSign * slot;
  p.pos.y = y;
  p.vel.x = 0;
  p.vel.y = 0;
  p.moveDir.x = 0;
  p.moveDir.y = 0;
  p.hasPuck = false;
  p.possessionQuality = 0;
  p.action = "Idle";
  p.actionTimer = 0;
  p.stunTicks = 0;
  p.recoveryTicks = 0;
  p.shootHoldTicks = 0;
  p.passHoldTicks = 0;
  p.receivedPassFromTick = -1;
  p.isProtecting = false;
}
